import { api } from '../client';

export type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface Approval {
  id: string;
  entityType: string;
  entityId: string;
  purchaseOrderId?: string;
  purchaseOrder?: {
    id: string;
    poNumber: string;
    currency?: string;
    totalAmount?: number;
    supplier?: { id: string; name: string };
  };
  requesterId: string;
  approverId?: string;
  approver?: { id: string; fullName: string; email: string };
  status: ApprovalStatus;
  comments?: string;
  requestedAt: string;
  respondedAt?: string;
}

export interface QueryApprovalParams {
  status?: ApprovalStatus;
  entityType?: string;
  requesterId?: string;
  approverId?: string;
  page?: number;
  limit?: number;
}

export interface PendingApprovalSummary {
  total: number;
  breakdown: { source: string; count: number; href: string }[];
}

export const approvalsApi = {
  async getAll(params?: QueryApprovalParams) {
    const response = await api.get<{
      data: Approval[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>('/approvals', { params });
    return response.data;
  },

  async getPendingCount() {
    const response = await api.get<number>('/approvals/pending/count');
    return response.data;
  },

  /**
   * Everything waiting on a decision, across all three queues.
   *
   * `getPendingCount` above counts purchase orders alone; this adds consumption
   * variances and freight awards, which used to be visible only from inside
   * their own modules.
   */
  async getPendingSummary() {
    const response = await api.get<PendingApprovalSummary>('/approvals/pending/summary');
    return response.data;
  },

  async getById(id: string) {
    const response = await api.get<Approval>(`/approvals/${id}`);
    return response.data;
  },

  async decide(id: string, decision: 'APPROVED' | 'REJECTED', comments?: string) {
    const response = await api.post<Approval>(`/approvals/${id}/decide`, {
      decision,
      comments,
    });
    return response.data;
  },
};
